import React, { useState, useEffect } from 'react';
import { AlertCircle, Loader2, ArrowRight, Check } from 'lucide-react';
import { supabaseHelpers } from '../lib/supabase';
import { toast } from 'sonner';

// Brand-aligned auth screen. Matches the landing page theme: light
// background, Space Mono headlines, Manrope body, electric purple CTA,
// Memphis-style decorative accents. Sign-up collects extended profile
// metadata that flows through to Supabase auth.users.raw_user_meta_data.

// Translations for all UI copy. Keyed by language code; the component
// picks based on URL prefix (/ar/* → 'ar', else 'en').
const T = {
  en: {
    eyebrowReset: '— Reset password',
    eyebrowSignUp: '— Create your account',
    eyebrowSignIn: '— Welcome back',
    titleResetA: 'Forgot it.',  titleResetB: 'It happens.',
    titleSignUpA: 'Get a branded first draft', titleSignUpB: 'in minutes.',
    titleSignInA: 'Pick up where you', titleSignInB: 'left off.',
    subReset: 'Enter your email and we’ll send a link to reset your password.',
    subSignUp: 'Set up your Wizardly.ai workspace. Two minutes, then you’re creating.',
    subSignIn: 'Sign in to your Wizardly.ai workspace.',
    fullName: 'Full name', fullNamePh: '',
    iAmA: 'I am a', iAmAPh: 'Select one',
    useCasesPh: 'Select one or more',
    tellMore: 'Tell us a bit more', tellMorePh: 'Researcher, freelancer, founder…',
    jobTitle: 'Job title', jobTitlePh: 'Strategy Lead',
    useCases: 'What do you plan to use Wizardly.ai for?',
    industry: 'Industry', industryPh: 'Select industry',
    industryOtherLbl: 'Tell us your industry', industryOtherPh: 'Architecture, hospitality, healthcare…',
    companySize: 'Company size', companySizePh: 'Select size',
    optional: '(optional)',
    workEmail: 'Work email', emailLbl: 'Email',
    emailPhPro: 'you@company.com', emailPhStudent: 'you@university.edu',
    password: 'Password', passwordHint: 'at least 6 characters',
    keepSignedIn: 'Keep me signed in',
    forgotPw: 'Forgot password?',
    btnSendReset: 'Send reset link', btnCreate: 'Create account', btnSignIn: 'Sign in',
    loadingReset: 'Sending reset link…', loadingCreate: 'Creating account…', loadingSignIn: 'Signing in…',
    backToSignIn: 'Back to sign in',
    haveAccount: 'Already have an account?', signInLink: 'Sign in',
    noAccount: 'Don’t have an account?', createOne: 'Create one',
    footTermsCreate: 'By creating an account you agree to our terms and privacy policy.',
    footTermsSignIn: 'By signing in you agree to our terms and privacy policy.',
    offlineNote: 'Supabase is offline. Using local admin access.',
    errFullName: 'Full name is required',
    errEmail: 'Email is required',
    errPassword: 'Password must be at least 6 characters',
    errUserType: 'Tell us if you’re a student, a professional, or other',
    errJobTitle: 'Job title is required',
    errUserTypeOther: 'Tell us a bit about your role',
    errUseCases: 'Pick at least one thing you plan to use Wizardly.ai for',
    errIndustryOther: 'Tell us your industry',
    errEnterEmail: 'Enter your email first',
    toastCreated: 'Account created. Check your email to verify.',
    toastWelcome: 'Welcome back.',
    toastReset: 'Reset link sent. Check your email.',
    toastResetFail: 'Failed to send reset link',
    toastAuthFail: 'Authentication failed',
    toastOffline: 'Signed in with offline admin access.',
    langSwitch: 'AR', langSwitchTitle: 'العربية',
    // Check-your-email card shown after a successful signup.
    checkEyebrow: '— Welcome aboard',
    checkTitleA: "You're",  checkTitleB: 'in.',
    checkBodyA: 'We sent a confirmation link to',
    checkBodyB: 'Confirm your email within 48 hours to keep your account active — but feel free to start using Wizardly right now.',
    checkSpamNote: 'Didn’t get the email? Check your spam folder, or resend the link below.',
    resendBtn: 'Resend confirmation email',
    goToToolBtn: 'Go to the tool →',
    resendingBtn: 'Resending…',
    resendCooldown: 'You can resend in {s}s',
    resendSent: 'Confirmation email resent. Check your inbox.',
    resendFail: 'Failed to resend. Try again in a moment.',
    backToSignInBtn: 'Back to sign in',
    wrongEmail: 'Wrong email? Start over',
  },
  ar: {
    eyebrowReset: '— استعادة كلمة المرور',
    eyebrowSignUp: '— أنشئ حسابك',
    eyebrowSignIn: '— أهلًا بعودتك',
    titleResetA: 'نسيتها.',  titleResetB: 'يحدث للجميع.',
    titleSignUpA: 'احصل على مسودة بهوية علامتك', titleSignUpB: 'في دقائق.',
    titleSignInA: 'تابع من حيث', titleSignInB: 'توقّفت.',
    subReset: 'أدخل بريدك الإلكتروني وسنرسل لك رابطًا لاستعادة كلمة المرور.',
    subSignUp: 'هيّئ مساحة عمل Wizardly.ai. دقيقتان، ثم تبدأ الإنشاء.',
    subSignIn: 'سجِّل الدخول إلى مساحة عمل Wizardly.ai.',
    fullName: 'الاسم الكامل', fullNamePh: '',
    iAmA: 'أنا', iAmAPh: 'اختر',
    useCasesPh: 'اختر واحدًا أو أكثر',
    tellMore: 'أخبرنا أكثر', tellMorePh: 'باحث، مستقل، مؤسس…',
    jobTitle: 'المسمى الوظيفي', jobTitlePh: 'مدير استراتيجية',
    useCases: 'ما الذي تخطط لاستخدام Wizardly.ai من أجله؟',
    industry: 'القطاع', industryPh: 'اختر القطاع',
    industryOtherLbl: 'أخبرنا عن قطاعك', industryOtherPh: 'هندسة معمارية، ضيافة، رعاية صحية…',
    companySize: 'حجم الشركة', companySizePh: 'اختر الحجم',
    optional: '(اختياري)',
    workEmail: 'البريد المهني', emailLbl: 'البريد الإلكتروني',
    emailPhPro: 'you@company.com', emailPhStudent: 'you@university.edu',
    password: 'كلمة المرور', passwordHint: '٦ أحرف على الأقل',
    keepSignedIn: 'أبقِني مسجَّل الدخول',
    forgotPw: 'نسيت كلمة المرور؟',
    btnSendReset: 'إرسال رابط الاستعادة', btnCreate: 'إنشاء الحساب', btnSignIn: 'تسجيل الدخول',
    loadingReset: 'جاري إرسال الرابط…', loadingCreate: 'جاري إنشاء الحساب…', loadingSignIn: 'جاري تسجيل الدخول…',
    backToSignIn: 'العودة إلى تسجيل الدخول',
    haveAccount: 'لديك حساب بالفعل؟', signInLink: 'تسجيل الدخول',
    noAccount: 'ليس لديك حساب؟', createOne: 'أنشئ حسابًا',
    footTermsCreate: 'بإنشاء حساب فإنك توافق على شروط الخدمة وسياسة الخصوصية.',
    footTermsSignIn: 'بتسجيل الدخول فإنك توافق على شروط الخدمة وسياسة الخصوصية.',
    offlineNote: 'Supabase غير متصل. يتم استخدام صلاحيات المسؤول المحلية.',
    errFullName: 'الاسم الكامل مطلوب',
    errEmail: 'البريد الإلكتروني مطلوب',
    errPassword: 'كلمة المرور يجب أن تكون ٦ أحرف على الأقل',
    errUserType: 'أخبرنا إن كنت طالبًا أو محترفًا أو آخر',
    errJobTitle: 'المسمى الوظيفي مطلوب',
    errUserTypeOther: 'أخبرنا قليلًا عن دورك',
    errUseCases: 'اختر شيئًا واحدًا على الأقل تخطط لاستخدام Wizardly.ai من أجله',
    errIndustryOther: 'أخبرنا عن قطاعك',
    errEnterEmail: 'أدخل بريدك الإلكتروني أولًا',
    toastCreated: 'تم إنشاء الحساب. تحقق من بريدك للتأكيد.',
    toastWelcome: 'مرحبًا بعودتك.',
    toastReset: 'تم إرسال رابط الاستعادة. تحقق من بريدك.',
    toastResetFail: 'فشل إرسال رابط الاستعادة',
    toastAuthFail: 'فشل التحقق',
    toastOffline: 'تم تسجيل الدخول بصلاحيات المسؤول المحلية.',
    langSwitch: 'EN', langSwitchTitle: 'English',
    // بطاقة "تحقق من بريدك" التي تظهر بعد إنشاء الحساب بنجاح.
    checkEyebrow: '— أهلًا بك',
    checkTitleA: 'تم',  checkTitleB: 'الانضمام.',
    checkBodyA: 'أرسلنا رابط تأكيد إلى',
    checkBodyB: 'يرجى تأكيد بريدك الإلكتروني خلال 48 ساعة للحفاظ على حسابك — يمكنك البدء باستخدام Wizardly الآن مباشرة.',
    checkSpamNote: 'لا ترى الرسالة؟ تحقق من مجلد البريد العشوائي، أو أعد إرسال الرابط أدناه.',
    resendBtn: 'إعادة إرسال رابط التأكيد',
    goToToolBtn: 'انتقل إلى الأداة ←',
    resendingBtn: 'جاري الإرسال…',
    resendCooldown: 'يمكنك إعادة الإرسال خلال {s} ثانية',
    resendSent: 'تم إعادة إرسال رابط التأكيد. تحقق من بريدك.',
    resendFail: 'فشل إعادة الإرسال. حاول مجددًا بعد قليل.',
    backToSignInBtn: 'العودة إلى تسجيل الدخول',
    wrongEmail: 'بريد خاطئ؟ ابدأ من جديد',
  },
};

export function LoginPage({ onLogin }) {
  // Language detection — /ar/login and /ar/signup serve the same
  // component in Arabic with RTL layout. Anything else is English.
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isAr = pathname.startsWith('/ar/');
  const lang = isAr ? 'ar' : 'en';
  const t = T[lang];

  // Mode + auth basics. /signup and /ar/signup default to sign-up view;
  // /login and /ar/login default to sign-in. /thankyou (and /ar/thankyou)
  // is the post-signup landing surface — shows the "check your inbox"
  // card with the email taken from the ?email= query param so refresh
  // keeps the user on the right step.
  const isThankYouRoute = pathname === '/thankyou' || pathname === '/ar/thankyou';
  const [isSignUp, setIsSignUp] = useState(
    pathname === '/signup' || pathname === '/ar/signup'
  );
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');

  // Credentials
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // After a successful signup we route the user to /thankyou and show
  // a persistent "check your inbox" card. The state stores the email we
  // sent the confirmation link to so the card can show it and the
  // resend button can re-target the same address. Pre-populated from
  // the URL when the user lands on /thankyou directly (e.g. refresh).
  const initialThankYouEmail = isThankYouRoute && typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('email') || ''
    : '';
  const [signupSuccessEmail, setSignupSuccessEmail] = useState(initialThankYouEmail);
  // Held user object from the auto-sign-in we run right after signUp.
  // Powers the "Go to the tool" button — clicking it calls onLogin
  // with this user so the App.js auth state flips and the dashboard
  // loads. Null when the user landed on /thankyou via direct URL
  // (refresh, deep link) since we don't have the password to sign in.
  const [signupUserPending, setSignupUserPending] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0); // seconds until next resend allowed
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = setInterval(() => setResendCooldown(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // Segment-friendly page_view for the auth surface. GA4 Enhanced Measurement
  // already records the generic page_view; this adds a `mode` dimension
  // (signin / signup / check_email) and `lang` so we can build clean
  // funnels in Looker Studio without scraping URL paths.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mode = signupSuccessEmail
      ? 'check_email'
      : isForgotPassword
        ? 'forgot_password'
        : isSignUp ? 'signup' : 'signin';
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'auth_page_view',
      page_type: 'auth',
      auth_mode: mode,
      lang: isAr ? 'ar' : 'en',
    });
  }, [isSignUp, isForgotPassword, signupSuccessEmail, isAr]);

  const offlineMode = process.env.REACT_APP_ALLOW_OFFLINE_AUTH === 'true';

  const validateSignUp = () => {
    if (!email.trim()) return t.errEmail;
    if (!password || password.length < 6) return t.errPassword;
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const fallbackLogin = () => {
      const offlineUser = {
        id: 'offline-admin',
        email: email || 'admin@prezlab.local',
        role: 'admin',
        source: 'offline',
      };
      toast.success(t.toastOffline);
      onLogin(offlineUser);
    };

    if (isSignUp) {
      const v = validateSignUp();
      if (v) { setError(v); return; }
    }

    setIsLoading(true);
    try {
      if (offlineMode) { fallbackLogin(); return; }
      const normalizedEmail = email.trim().toLowerCase();

      if (isSignUp) {
        // Minimal metadata — full profile capture moved post-signup so
        // the front door is just email + password. 'other' is the safest
        // default for the user_type CHECK constraint on user_profiles.
        const metadata = { user_type: 'other' };
        const result = await supabaseHelpers.signUp(normalizedEmail, password, metadata);
        if (result.error) throw result.error;
        if (result.user) {
          // GTM/GA4 conversion event. Mark sign_up_complete as a Key Event in GA4.
          if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'sign_up_complete',
              lang: isAr ? 'ar' : 'en',
            });
          }
          toast.success(t.toastCreated);
          // Auto-sign-in so the user lands in the app immediately instead
          // of waiting on email confirmation. Requires the Supabase
          // "Enable email confirmations" setting to be OFF — otherwise
          // signIn will reject the unconfirmed account and we fall back
          // to showing the check-email card.
          let activeUser = result.user;
          try {
            const signInResult = await supabaseHelpers.signIn(normalizedEmail, password);
            if (signInResult && !signInResult.error && signInResult.user) {
              activeUser = signInResult.user;
            }
          } catch (_) { /* signIn failure → user stays on /thankyou and can resend */ }
          // Hand off to /thankyou so refresh keeps the user on the
          // welcome step. The page shows a "you're in, confirm within
          // 48 hours" message plus a Go-to-the-tool button that calls
          // onLogin to enter the app.
          setSignupSuccessEmail(normalizedEmail);
          setSignupUserPending(activeUser);
          setResendCooldown(30); // 30s before they can resend
          const thankYouPath = isAr ? '/ar/thankyou' : '/thankyou';
          const next = `${thankYouPath}?email=${encodeURIComponent(normalizedEmail)}`;
          try { window.history.replaceState(null, '', next); } catch (_) { /* no-op */ }
          if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'signup_check_email_shown',
              lang: isAr ? 'ar' : 'en',
            });
          }
        }
      } else {
        const result = await supabaseHelpers.signIn(normalizedEmail, password);
        if (result.error) throw result.error;
        if (result.user) {
          if (rememberMe) localStorage.setItem('prezlab_remember_me', 'true');
          else localStorage.removeItem('prezlab_remember_me');
          toast.success(t.toastWelcome);
          onLogin(result.user);
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      if (offlineMode) fallbackLogin();
      else {
        setError(err.message || t.toastAuthFail);
        toast.error(err.message || t.toastAuthFail);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Resend the signup-confirmation email. Bounded by a client-side cooldown
  // so users can't spam Supabase's transactional pipeline.
  const handleResendConfirmation = async () => {
    if (!signupSuccessEmail || resending || resendCooldown > 0) return;
    setResending(true);
    try {
      const { error: resendError } = await supabaseHelpers.resendSignupConfirmation(signupSuccessEmail);
      if (resendError) throw resendError;
      toast.success(t.resendSent);
      setResendCooldown(60);
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'signup_resend_clicked',
          lang: isAr ? 'ar' : 'en',
        });
      }
    } catch (err) {
      console.error('Resend error:', err);
      toast.error(err.message || t.resendFail);
    } finally {
      setResending(false);
    }
  };

  // Wipe the post-signup state so the user can start over (e.g. they used
  // the wrong email and want to re-sign-up) or fall back to sign-in.
  const exitCheckEmail = (toSignIn = true) => {
    setSignupSuccessEmail('');
    setResendCooldown(0);
    setIsSignUp(!toSignIn);
    setError('');
    // Move off /thankyou so refresh doesn't drop the user back on the
    // confirmation card.
    const nextPath = isAr
      ? (toSignIn ? '/ar/login' : '/ar/signup')
      : (toSignIn ? '/login' : '/signup');
    try { window.history.replaceState(null, '', nextPath); } catch (_) { /* no-op */ }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setError(t.errEnterEmail); return; }
    setIsLoading(true);
    setError('');
    try {
      const { error: resetError } = await supabaseHelpers.resetPassword(email.trim().toLowerCase());
      if (resetError) throw resetError;
      toast.success(t.toastReset);
      setIsForgotPassword(false);
    } catch (err) {
      setError(err.message || t.toastResetFail);
      toast.error(err.message || t.toastResetFail);
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = () => {
    setError('');
    setIsForgotPassword(false);
    setIsSignUp(prev => !prev);
  };

  return (
    <>
      <style>{`
        .auth-page{min-height:100vh;background:#FFFFFF;color:#0A0414;font-family:'Manrope',sans-serif;display:flex;flex-direction:column;align-items:center;padding:48px 24px;position:relative;overflow-x:hidden;}
        .auth-page::before,.auth-page::after{content:'';position:fixed;pointer-events:none;z-index:0;}
        .auth-page::before{top:-120px;right:-80px;width:280px;height:280px;border:6px solid #F19FFF;border-radius:50%;opacity:.4;}
        .auth-page::after{bottom:-100px;left:-60px;width:180px;height:34px;background:#4000FF;transform:rotate(-22deg);border-radius:4px;opacity:.65;}
        .auth-card{position:relative;z-index:1;width:100%;max-width:520px;}
        .auth-logo{display:block;height:36px;width:auto;margin:0 auto 32px;}
        .auth-eyebrow{font-family:'Space Mono',monospace;font-size:13px;font-weight:700;color:#7B5BFF;letter-spacing:.5px;margin-bottom:14px;text-align:center;}
        .auth-title{font-family:'Space Mono',monospace;font-size:32px;font-weight:700;line-height:1.12;letter-spacing:-1.2px;color:#0A0414;text-align:center;margin-bottom:14px;}
        .auth-title .acc{color:#7B5BFF;}
        .auth-sub{font-size:15px;color:rgba(10,4,20,.66);line-height:1.6;text-align:center;margin-bottom:36px;max-width:440px;margin-left:auto;margin-right:auto;}
        .auth-form{background:#FFFFFF;border:1px solid rgba(10,4,20,.08);border-radius:16px;padding:32px;box-shadow:0 18px 48px rgba(64,0,255,.06);}
        .auth-row{margin-bottom:18px;}
        .auth-row.split{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
        @media(max-width:520px){.auth-row.split{grid-template-columns:1fr;}}
        .auth-label{display:block;font-family:'Space Mono',monospace;font-size:11px;font-weight:700;color:#0A0414;letter-spacing:.5px;margin-bottom:8px;text-transform:uppercase;}
        .auth-label .opt{color:rgba(10,4,20,.4);font-weight:400;text-transform:none;letter-spacing:0;margin-left:6px;}
        .auth-input,.auth-select{width:100%;padding:12px 14px;border:1px solid rgba(10,4,20,.14);border-radius:10px;font-family:'Manrope',sans-serif;font-size:14px;background:#FFFFFF;color:#0A0414;transition:border .15s,box-shadow .15s;}
        .auth-input:focus,.auth-select:focus{outline:none;border-color:#4000FF;box-shadow:0 0 0 3px rgba(64,0,255,.12);}
        /* Multi-select dropdown — used for the "use cases" question. */
        .auth-multiselect{position:relative;width:100%;}
        .multiselect-btn{display:flex;align-items:center;justify-content:space-between;gap:8px;text-align:start;cursor:pointer;}
        .multiselect-btn:disabled{opacity:.6;cursor:not-allowed;}
        .multiselect-btn.open{border-color:#4000FF;box-shadow:0 0 0 3px rgba(64,0,255,.12);}
        .multiselect-summary{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .multiselect-placeholder{flex:1;color:rgba(10,4,20,.4);}
        .multiselect-chev{flex-shrink:0;color:rgba(10,4,20,.5);transition:transform .15s;}
        .multiselect-chev.open{transform:rotate(180deg);}
        .multiselect-panel{position:absolute;top:calc(100% + 6px);left:0;right:0;background:#FFFFFF;border:1px solid rgba(10,4,20,.14);border-radius:10px;box-shadow:0 12px 32px rgba(10,4,20,.14);padding:6px;z-index:50;max-height:260px;overflow-y:auto;}
        .multiselect-option{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:8px;cursor:pointer;font-size:14px;color:#0A0414;transition:background .12s;}
        .multiselect-option:hover{background:rgba(64,0,255,.06);}
        .multiselect-option.on{background:rgba(64,0,255,.05);}
        .multiselect-option input[type=checkbox]{position:absolute;opacity:0;pointer-events:none;}
        .multiselect-checkbox{display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border:1.5px solid rgba(10,4,20,.24);border-radius:5px;flex-shrink:0;color:#FFFFFF;background:#FFFFFF;transition:all .12s;}
        .multiselect-option.on .multiselect-checkbox{background:#4000FF;border-color:#4000FF;}
        .auth-pills{display:flex;flex-wrap:wrap;gap:8px;}
        .auth-pill{padding:8px 14px;border:1px solid rgba(10,4,20,.14);border-radius:100px;font-family:'Space Mono',monospace;font-size:12px;font-weight:700;letter-spacing:.5px;background:#FFFFFF;color:#0A0414;cursor:pointer;transition:all .15s;display:inline-flex;align-items:center;gap:6px;}
        .auth-pill:hover{border-color:#4000FF;color:#4000FF;}
        .auth-pill.active{background:#4000FF;color:#FFFFFF;border-color:#4000FF;}
        .auth-pill .check{width:14px;height:14px;}
        .auth-cta{width:100%;padding:14px 24px;border-radius:100px;font-family:'Space Mono',monospace;font-size:14px;font-weight:700;letter-spacing:.3px;background:#4000FF;color:#FFFFFF;border:none;cursor:pointer;transition:all .2s;display:flex;align-items:center;justify-content:center;gap:8px;margin-top:8px;}
        .auth-cta:hover:not(:disabled){background:#3000CC;transform:translateY(-1px);box-shadow:0 12px 32px rgba(64,0,255,.30);}
        .auth-cta:disabled{opacity:.5;cursor:not-allowed;}
        .auth-error{display:flex;align-items:center;gap:8px;padding:12px 14px;background:rgba(220,38,38,.08);border:1px solid rgba(220,38,38,.2);border-radius:10px;color:#C73E1D;font-size:13px;margin-bottom:18px;}
        .auth-toggle{text-align:center;margin-top:20px;font-size:13px;color:rgba(10,4,20,.66);}
        .auth-toggle button{background:none;border:none;color:#4000FF;font-weight:700;cursor:pointer;font-family:inherit;font-size:inherit;padding:0;text-decoration:underline;text-underline-offset:2px;}
        .auth-toggle button:hover{color:#3000CC;}
        .auth-meta-row{display:flex;justify-content:space-between;align-items:center;margin-top:-8px;margin-bottom:18px;font-size:12px;}
        .auth-remember{display:flex;align-items:center;gap:8px;cursor:pointer;color:rgba(10,4,20,.66);}
        .auth-remember input{accent-color:#4000FF;}
        .auth-link{background:none;border:none;color:rgba(10,4,20,.66);font-family:inherit;font-size:inherit;cursor:pointer;padding:0;text-decoration:none;}
        .auth-link:hover{color:#4000FF;}
        .auth-offline{font-size:12px;color:#C0913E;background:rgba(192,145,62,.10);padding:8px 12px;border-radius:8px;margin-bottom:14px;text-align:center;}
        .auth-foot{margin-top:24px;text-align:center;font-size:11px;color:rgba(10,4,20,.4);}
        /* Post-signup "check your inbox" card. */
        .check-card{background:#FFFFFF;border:1px solid rgba(10,4,20,.08);border-radius:16px;padding:32px;box-shadow:0 18px 48px rgba(64,0,255,.06);text-align:center;}
        .check-icon{display:inline-flex;align-items:center;justify-content:center;width:64px;height:64px;border-radius:50%;background:rgba(64,0,255,.08);color:#4000FF;margin-bottom:18px;}
        .check-body{font-size:15px;color:rgba(10,4,20,.72);line-height:1.62;margin-bottom:14px;}
        .check-email{display:inline-block;font-family:'Space Mono',monospace;font-weight:700;color:#0A0414;padding:6px 12px;border-radius:8px;background:rgba(64,0,255,.06);margin:6px 0;word-break:break-all;}
        .check-spam{font-size:13px;color:rgba(10,4,20,.5);line-height:1.6;margin:22px 0 18px;}
        .check-cta-row{display:flex;flex-direction:column;gap:10px;margin-top:8px;}
        .check-cta-secondary{display:inline-block;text-align:center;padding:13px 24px;border-radius:100px;font-family:'Space Mono',monospace;font-size:13px;font-weight:700;letter-spacing:.3px;color:#4000FF;background:#FFFFFF;border:1px solid rgba(64,0,255,.24);cursor:pointer;transition:all .15s;text-decoration:none;}
        .check-cta-secondary:hover:not(:disabled){background:rgba(64,0,255,.06);}
        .check-cta-secondary:disabled{opacity:.5;cursor:not-allowed;}
        .check-back{margin-top:14px;font-size:13px;color:rgba(10,4,20,.5);}
        .check-back button{background:none;border:none;color:#4000FF;font-weight:600;cursor:pointer;font-family:inherit;font-size:inherit;padding:0;text-decoration:underline;text-underline-offset:2px;}
      `}</style>

      <div className="auth-page" dir={isAr ? 'rtl' : 'ltr'} style={{fontFamily: isAr ? "'Tajawal',sans-serif" : undefined}}>
        <div className="auth-card">
          <a href="/" aria-label="Wizardly.ai home">
            <img src="/logo/wizardly-full.svg" alt="Wizardly.ai" className="auth-logo" />
          </a>

          {/* Language toggle pill — preserves current mode (signup vs login) */}
          <div style={{textAlign:'center',marginBottom:'16px'}}>
            <a href={(isAr ? '' : '/ar') + (isSignUp ? '/signup' : '/login')} title={t.langSwitchTitle}
              style={{fontFamily:"'Space Mono',monospace",fontSize:'11px',fontWeight:700,color:'#7B5BFF',textDecoration:'none',padding:'4px 12px',border:'1px solid rgba(10,4,20,.14)',borderRadius:'100px'}}>
              {t.langSwitch}
            </a>
          </div>

          {signupSuccessEmail ? (
            <>
              <div className="auth-eyebrow">{t.checkEyebrow}</div>
              <h1 className="auth-title">{t.checkTitleA} <span className="acc">{t.checkTitleB}</span></h1>
            </>
          ) : isForgotPassword ? (
            <>
              <div className="auth-eyebrow">{t.eyebrowReset}</div>
              <h1 className="auth-title">{t.titleResetA} <span className="acc">{t.titleResetB}</span></h1>
              <p className="auth-sub">{t.subReset}</p>
            </>
          ) : isSignUp ? (
            <>
              <div className="auth-eyebrow">{t.eyebrowSignUp}</div>
              <h1 className="auth-title">{t.titleSignUpA} <span className="acc">{t.titleSignUpB}</span></h1>
              <p className="auth-sub">{t.subSignUp}</p>
            </>
          ) : (
            <>
              <div className="auth-eyebrow">{t.eyebrowSignIn}</div>
              <h1 className="auth-title">{t.titleSignInA} <span className="acc">{t.titleSignInB}</span></h1>
              <p className="auth-sub">{t.subSignIn}</p>
            </>
          )}

          {signupSuccessEmail ? (
            <div className="check-card">
              <div className="check-icon">
                <Check size={32} strokeWidth={2.5} />
              </div>
              <p className="check-body">
                {t.checkBodyA}
                <br />
                <span className="check-email">{signupSuccessEmail}</span>
                <br />
                {t.checkBodyB}
              </p>
              <p className="check-spam">{t.checkSpamNote}</p>
              <div className="check-cta-row">
                {/* Primary CTA: send the user into the app. Falls back
                    to /login when we don't have a pending user object
                    (e.g. they refreshed onto /thankyou via deep link). */}
                <button type="button" className="auth-cta"
                  onClick={() => {
                    if (signupUserPending && onLogin) {
                      onLogin(signupUserPending);
                    } else {
                      window.location.href = isAr ? '/ar/login' : '/login';
                    }
                  }}>
                  {t.goToToolBtn}
                </button>
                <button type="button" className="check-cta-secondary"
                  onClick={handleResendConfirmation}
                  disabled={resending || resendCooldown > 0}>
                  {resending
                    ? t.resendingBtn
                    : resendCooldown > 0
                      ? t.resendCooldown.replace('{s}', resendCooldown)
                      : t.resendBtn}
                </button>
              </div>
              <p className="check-back">
                <button type="button" onClick={() => exitCheckEmail(false)}>{t.wrongEmail}</button>
              </p>
            </div>
          ) : (
          <form className="auth-form" onSubmit={isForgotPassword ? handleForgotPassword : handleSubmit}>
            {offlineMode && (
              <div className="auth-offline">{t.offlineNote}</div>
            )}
            {error && (
              <div className="auth-error"><AlertCircle size={16} /><span>{error}</span></div>
            )}

            {/* Email — front door is just email + password. Profile
                capture happens post-signup so the sign-up flow stays
                a single fold. */}
            <div className="auth-row">
              <label className="auth-label" htmlFor="email">{t.emailLbl}</label>
              <input id="email" type="email" className="auth-input" autoComplete="email"
                value={email} onChange={e => setEmail(e.target.value)} disabled={isLoading} required
                placeholder={t.emailPhPro} />
            </div>

            {/* Password (hidden in forgot-password mode) */}
            {!isForgotPassword && (
              <div className="auth-row">
                <label className="auth-label" htmlFor="password">
                  {t.password} {isSignUp && <span className="opt">{t.passwordHint}</span>}
                </label>
                <input id="password" type="password" className="auth-input"
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  value={password} onChange={e => setPassword(e.target.value)} disabled={isLoading} required minLength={6}
                  placeholder="••••••••" />
              </div>
            )}

            {/* Sign-in extras: remember me + forgot password */}
            {!isSignUp && !isForgotPassword && (
              <div className="auth-meta-row">
                <label className="auth-remember">
                  <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} />
                  <span>{t.keepSignedIn}</span>
                </label>
                <button type="button" className="auth-link"
                  onClick={() => { setIsForgotPassword(true); setError(''); }}>
                  {t.forgotPw}
                </button>
              </div>
            )}

            <button type="submit" className="auth-cta" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {isForgotPassword ? t.loadingReset : isSignUp ? t.loadingCreate : t.loadingSignIn}
                </>
              ) : (
                <>
                  {isForgotPassword ? t.btnSendReset : isSignUp ? t.btnCreate : t.btnSignIn}
                  <ArrowRight size={16} style={isAr ? {transform:'scaleX(-1)'} : undefined} />
                </>
              )}
            </button>

            <div className="auth-toggle">
              {isForgotPassword ? (
                <button type="button" onClick={() => { setIsForgotPassword(false); setError(''); }}>
                  {t.backToSignIn}
                </button>
              ) : isSignUp ? (
                <span>{t.haveAccount} <button type="button" onClick={switchMode}>{t.signInLink}</button></span>
              ) : (
                <span>{t.noAccount} <button type="button" onClick={switchMode}>{t.createOne}</button></span>
              )}
            </div>
          </form>
          )}

          {!signupSuccessEmail && (
            <p className="auth-foot">
              {isSignUp ? t.footTermsCreate : t.footTermsSignIn}
            </p>
          )}
        </div>
      </div>
    </>
  );
}
